class GalleryPortfolio extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.swiper = null;
    this.currentImageIndex = 0;
    this.images = [];
    this.isOpen = false;
    this.initialCount = 6;
    this.loadMoreCount = 3;
    this.currentlyVisible = 0;
    this.loadMoreBtn = null;
  }

  connectedCallback() {
    this.initializeSettings();
    this.collectImages();
    this.createModal();
    this.bindEvents();
    this.updateAriaLabels();
    this.initializeLoadMore();
  }

  initializeSettings() {
    this.initialCount = parseInt(this.getAttribute('data-initial-count')) || 6;
    this.loadMoreCount = parseInt(this.getAttribute('data-load-more-count')) || 3;
  }

  collectImages() {
    const imageButtons = this.querySelectorAll('.gallery-portfolio__item:not(.gallery-portfolio__item--hidden)');
    this.images = Array.from(imageButtons).map(button => {
      const wrapper = button.querySelector('.gallery-portfolio__image-wrapper');
      const hasImage = wrapper?.getAttribute('data-has-image') === 'true';
      const img = button.querySelector('.gallery-portfolio__image:not(.gallery-portfolio__image--placeholder)');
      const placeholder = button.querySelector('.gallery-portfolio__image--placeholder');
      const title = button.querySelector('.gallery-portfolio__title');
      
      return {
        button,
        src: hasImage && img?.src ? img.src : '',
        alt: hasImage && img?.alt ? img.alt : 'Placeholder image',
        title: title?.textContent || 'Gallery Image',
        isPlaceholder: !hasImage,
        placeholderSvg: !hasImage && placeholder ? placeholder.innerHTML : null
      };
    });
  }

  updateAriaLabels() {
    this.images.forEach((image, index) => {
      image.button.setAttribute('aria-label', `Open image ${index + 1} of ${this.images.length}: ${image.title}`);
    });
    this.setAttribute('role', 'region');
    this.setAttribute('aria-label', 'Image gallery');
  }

  createModal() {
    this.modal = document.createElement('div');
    this.modal.className = 'gallery-portfolio__modal';
    this.modal.setAttribute('role', 'dialog');
    this.modal.setAttribute('aria-modal', 'true');
    this.modal.setAttribute('aria-label', 'Gallery lightbox');
    this.modal.innerHTML = `
      <div class="gallery-portfolio__modal-backdrop"></div>
      <div class="gallery-portfolio__modal-content">
        <button class="gallery-portfolio__modal-close" type="button" aria-label="Close gallery">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <div class="swiper gallery-portfolio__swiper">
          <div class="swiper-wrapper">
            ${this.images.map((image, index) => `
              <div class="swiper-slide">
                <div class="gallery-portfolio__slide-content">
                  <div class="gallery-portfolio__modal-image-container">
                    ${image.isPlaceholder ? 
                      `<div class="gallery-portfolio__modal-image gallery-portfolio__image--placeholder">
                        ${image.placeholderSvg || `<svg viewBox="0 0 525 525" xmlns="http://www.w3.org/2000/svg">
                          <rect fill="transparent" width="100%" height="100%"/>
                          <g fill="currentColor" fill-rule="nonzero">
                            <path d="M350 175v-25a25 25 0 0 0-25-25H200a25 25 0 0 0-25 25v25H100a25 25 0 0 0-25 25v225a25 25 0 0 0 25 25h325a25 25 0 0 0 25-25V200a25 25 0 0 0-25-25h-75zM225 175h75v-12.5h-75V175zM200 162.5h125V175H200v-12.5zm200 237.5H125V225h275v175z"/>
                            <circle cx="262.5" cy="275" r="50"/>
                            <circle cx="350" cy="250" r="12.5"/>
                          </g>
                        </svg>`}
                      </div>` : 
                      `<img src="${image.src.replace(/width=\d+/, 'width=1200')}" alt="${image.alt}" class="gallery-portfolio__modal-image" />`
                    }
                  </div>
                  <div class="gallery-portfolio__slide-info">
                    <h3 class="gallery-portfolio__modal-title">${image.title}</h3>
                    <span class="gallery-portfolio__modal-counter">1 / ${this.images.length}</span>
                  </div>
                </div>
              </div>
            `).join('')}
          </div>
          
          <div class="swiper-button-next gallery-portfolio__nav-button"></div>
          <div class="swiper-button-prev gallery-portfolio__nav-button"></div>
        </div>
      </div>
    `;
    document.body.appendChild(this.modal);
  }

  bindEvents() {
    // Bind image click events
    this.images.forEach((image, index) => {
      image.button.addEventListener('click', () => this.openModal(index));
    });

    // Bind modal events
    const closeBtn = this.modal.querySelector('.gallery-portfolio__modal-close');
    const backdrop = this.modal.querySelector('.gallery-portfolio__modal-backdrop');

    closeBtn.addEventListener('click', () => this.closeModal());
    backdrop.addEventListener('click', () => this.closeModal());

    // Keyboard events
    this.handleKeydown = (e) => {
      if (!this.isOpen) return;
      
      switch(e.key) {
        case 'Escape':
          this.closeModal();
          break;
        case 'ArrowLeft':
          this.swiper?.slidePrev();
          break;
        case 'ArrowRight':
          this.swiper?.slideNext();
          break;
      }
    };

    document.addEventListener('keydown', this.handleKeydown);
  }

  initializeLoadMore() {
    const allItems = this.querySelectorAll('.gallery-portfolio__item');
    this.loadMoreBtn = this.querySelector('.gallery-portfolio__load-more-btn');
    const loadMoreContainer = this.querySelector('.gallery-portfolio__load-more-container');
    
    // If we have more items than the initial count, set up load more
    if (allItems.length > this.initialCount) {
      this.currentlyVisible = this.initialCount;
      
      // Hide items beyond initial count
      allItems.forEach((item, index) => {
        if (index >= this.initialCount) {
          item.classList.add('gallery-portfolio__item--hidden');
        }
      });
      
      // Show load more container and bind click event
      if (loadMoreContainer && this.loadMoreBtn) {
        loadMoreContainer.style.display = 'block';
        this.loadMoreBtn.addEventListener('click', () => this.loadMoreImages());
      }
    } else {
      // Hide load more container if not needed
      if (loadMoreContainer) {
        loadMoreContainer.style.display = 'none';
      }
      this.currentlyVisible = allItems.length;
    }
  }

  loadMoreImages() {
    const allItems = this.querySelectorAll('.gallery-portfolio__item');
    const hiddenItems = this.querySelectorAll('.gallery-portfolio__item--hidden');
    const itemsToShow = Math.min(this.loadMoreCount, hiddenItems.length);
    
    // Show the next batch of items
    for (let i = 0; i < itemsToShow; i++) {
      if (hiddenItems[i]) {
        hiddenItems[i].classList.remove('gallery-portfolio__item--hidden');
        this.currentlyVisible++;
      }
    }
    
    // Hide load more button if all items are now visible
    if (this.currentlyVisible >= allItems.length) {
      const loadMoreContainer = this.querySelector('.gallery-portfolio__load-more-container');
      if (loadMoreContainer) {
        loadMoreContainer.style.display = 'none';
      }
    }
    
    // Re-collect images and update modal to include newly visible items
    this.collectImages();
    this.updateModal();
    this.updateAriaLabels();
  }

  updateModal() {
    // Remove existing modal and create a new one with updated images
    if (this.modal && this.modal.parentNode) {
      this.modal.parentNode.removeChild(this.modal);
    }
    this.createModal();
    
    // Re-bind modal events
    const closeBtn = this.modal.querySelector('.gallery-portfolio__modal-close');
    const backdrop = this.modal.querySelector('.gallery-portfolio__modal-backdrop');
    
    closeBtn.addEventListener('click', () => this.closeModal());
    backdrop.addEventListener('click', () => this.closeModal());
  }

  openModal(index) {
    this.currentImageIndex = index;
    this.isOpen = true;
    
    this.modal.classList.add('gallery-portfolio__modal--active');
    document.body.style.overflow = 'hidden';
    
    // Initialize Swiper
    if (window.Swiper && !this.swiper) {
      // Hide the swiper during initialization to prevent flicker
      const swiperEl = this.modal.querySelector('.gallery-portfolio__swiper');
      if (swiperEl) {
        swiperEl.style.opacity = '0';
      }
      
      setTimeout(() => {
        this.swiper = new Swiper('.gallery-portfolio__swiper', {
          initialSlide: index,
          spaceBetween: 20,
          loop: this.images.length > 1,
          speed: 400,
          grabCursor: false,
          touchRatio: 1,
          touchAngle: 45,
          threshold: 5,
          simulateTouch: true,
          allowTouchMove: true,
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
          navigation: {
            nextEl: '.gallery-portfolio__nav-button.swiper-button-next',
            prevEl: '.gallery-portfolio__nav-button.swiper-button-prev',
            disabledClass: 'swiper-button-disabled',
          },
          updateOnWindowResize: true,
          resizeObserver: true,
          on: {
            init: (swiper) => {
              // Show swiper after initialization to prevent flicker
              if (swiperEl) {
                swiperEl.style.opacity = '1';
              }
            },
            slideChange: (swiper) => {
              this.currentImageIndex = swiper.realIndex;
              this.updateSlideInfo();
            },
            touchStart: () => document.body.classList.add('gallery-swiping'),
            touchEnd: () => {
              setTimeout(() => {
                document.querySelectorAll('.swiper-slide').forEach(slide => {
                  slide.classList.remove('dragging');
                });
                document.body.classList.remove('gallery-swiping');
              }, 100);
            }
          }
        });
        
        this.updateSlideInfo();
        this.setupNavigationButtons();
      }, 50); // Reduced timeout for faster initialization
    }
    
    // Focus management
    this.modal.setAttribute('aria-hidden', 'false');
    this.modal.querySelector('.gallery-portfolio__modal-close')?.focus();
  }

  closeModal() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    
    // Immediately freeze Swiper in current position to prevent any slide changes
    if (this.swiper) {
      // Disable all Swiper functionality immediately
      this.swiper.disable();
      this.swiper.allowSlideNext = false;
      this.swiper.allowSlidePrev = false;
      this.swiper.allowTouchMove = false;
      
      // Force current slide to stay active during close
      const currentSlide = this.swiper.slides[this.currentImageIndex];
      if (currentSlide) {
        // Remove active class from all slides
        this.swiper.slides.forEach(slide => {
          slide.classList.remove('swiper-slide-active', 'swiper-slide-next', 'swiper-slide-prev');
        });
        // Add active class only to current slide
        currentSlide.classList.add('swiper-slide-active');
      }
    }
    
    // Start modal fade-out immediately
    this.modal.classList.remove('gallery-portfolio__modal--active');
    document.body.style.overflow = '';
    
    // Destroy Swiper after fade animation completes
    setTimeout(() => {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }, 350);
    
    this.modal.setAttribute('aria-hidden', 'true');

    // Return focus to the gallery button that was clicked
    setTimeout(() => {
      this.images[this.currentImageIndex]?.button?.focus();
    }, 400);
  }

  updateSlideInfo() {
    const currentSlide = this.modal.querySelector('.swiper-slide-active');
    if (!currentSlide) return;
    
    const titleElement = currentSlide.querySelector('.gallery-portfolio__modal-title');
    const counterElement = currentSlide.querySelector('.gallery-portfolio__modal-counter');
    const currentImage = this.images[this.currentImageIndex];
    
    if (titleElement && currentImage) {
      const titleText = titleElement.childNodes[0];
      if (titleText) {
        titleText.textContent = `${currentImage.title} `;
      }
    }
    
    if (counterElement) {
      counterElement.textContent = `${this.currentImageIndex + 1} / ${this.images.length}`;
    }

    if (currentImage) {
      this.modal.setAttribute('aria-label', `Gallery lightbox: ${currentImage.title}, image ${this.currentImageIndex + 1} of ${this.images.length}`);
    }
  }

  setupNavigationButtons() {
    const nextBtn = this.modal.querySelector('.swiper-button-next');
    const prevBtn = this.modal.querySelector('.swiper-button-prev');
    
    if (nextBtn) {
      nextBtn.innerHTML = '';
      nextBtn.setAttribute('aria-label', 'Next image');
    }
    
    if (prevBtn) {
      prevBtn.innerHTML = '';
      prevBtn.setAttribute('aria-label', 'Previous image');
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.handleKeydown) {
      document.removeEventListener('keydown', this.handleKeydown);
    }
    
    // Destroy Swiper instance
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    
    // Remove modal from DOM
    if (this.modal && this.modal.parentNode) {
      this.modal.parentNode.removeChild(this.modal);
    }
    
    // Reset body overflow if modal was open
    if (this.isOpen) {
      document.body.style.overflow = '';
    }
  }
}

customElements.define('gallery-portfolio', GalleryPortfolio);